import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import ReportTable, { RRow, RCell } from "components/Tables/ReportTable";
import { currencyFormatter, withoutSymbolFormatter } from "utilities/currencyFormatter";
import ReceiptIcon from "@mui/icons-material/Receipt";

const InvoiceConfirmView = ({ invoiceDescription, invoiceDetails, total, discount }) => {
  const theme  = useTheme();
  const isDark = theme.palette.mode === "dark";

  const cardBg  = isDark ? "rgba(255,255,255,0.03)" : "#ffffff";
  const accent  = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;
  const border  = isDark ? "rgba(29,158,117,0.14)" : "rgba(29,158,117,0.16)";

  const grandTotal = parseFloat(total) - parseFloat(discount || 0);

  const columns = [
    { label: "#",         width: 40  },
    { label: "Product"               },
    { label: "Unit"                  },
    { label: "Batch"                 },
    { label: "Qty",       align: "center" },
    { label: "Price",     align: "right"  },
    { label: "Discount",  align: "right"  },
    { label: "Total",     align: "right"  },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>

      {/* ── Header ── */}
      <Box sx={{
        display: "flex", alignItems: "center", gap: "10px",
        p: "1rem 1.25rem",
        backgroundColor: "rgba(29,158,117,0.06)",
        border: `1px solid ${border}`,
        borderRadius: "10px",
      }}>
        <Box sx={{ width: "34px", height: "34px", borderRadius: "9px", backgroundColor: "rgba(29,158,117,0.12)", display: "flex", alignItems: "center", justifyContent: "center", color: accent, flexShrink: 0 }}>
          <ReceiptIcon sx={{ fontSize: "18px" }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: "10px", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: textSec }}>
            Confirm bill
          </Typography>
          <Typography sx={{ fontFamily: "'DM Serif Display', serif", fontSize: "16px", fontWeight: 400, color: textPri }}>
            {invoiceDescription}
          </Typography>
        </Box>
      </Box>

      {/* ── Products section header ── */}
      <Box sx={{
        backgroundColor: cardBg,
        border: `1px solid ${border}`,
        borderRadius: "10px",
        overflow: "hidden",
      }}>
        <Box sx={{ px: "1.25rem", py: "0.75rem", borderBottom: `1px solid ${border}`, backgroundColor: isDark ? "rgba(29,158,117,0.05)" : "rgba(29,158,117,0.03)" }}>
          <Typography sx={{ fontSize: "10px", fontWeight: 500, letterSpacing: "0.08em", textTransform: "uppercase", color: accent }}>
            Products ({invoiceDetails?.length ?? 0} items)
          </Typography>
        </Box>
        <Box sx={{ p: "0.75rem 1rem" }}>
          <ReportTable
            columns={columns}
            rows={invoiceDetails ?? []}
            emptyMessage="No items on this bill"
            renderRow={(row, index) => (
              <RRow key={row.data_id ?? index} index={index}>
                <RCell>{index + 1}</RCell>
                <RCell>
                  <Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>
                    {row.product_name || "—"}
                  </Typography>
                </RCell>
                <RCell>{row.product_unit || "—"}</RCell>
                <RCell>{row.batch || "—"}</RCell>
                <RCell align="center">{row.quantity}</RCell>
                <RCell align="right">{withoutSymbolFormatter(parseFloat(row.sale_price || 0))}</RCell>
                <RCell align="right">{withoutSymbolFormatter(parseFloat(row.discount || 0))}</RCell>
                <RCell align="right">
                  <Typography sx={{ fontSize: "13px", fontWeight: 500, color: accent }}>
                    {withoutSymbolFormatter(parseFloat(row.total || 0))}
                  </Typography>
                </RCell>
              </RRow>
            )}
          />
        </Box>
      </Box>

      {/* ── Totals summary ── */}
      <Box sx={{
        backgroundColor: cardBg,
        border: `1px solid ${border}`,
        borderRadius: "10px",
        overflow: "hidden",
      }}>
        {[
          { label: "Sub-total",      value: currencyFormatter(parseFloat(total || 0)),    highlight: false },
          { label: "Total discount", value: currencyFormatter(parseFloat(discount || 0)), highlight: false },
          { label: "Grand total",    value: currencyFormatter(grandTotal),                highlight: true  },
        ].map(({ label, value, highlight }, i, arr) => (
          <Box key={label} sx={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            px: "1.25rem", py: "0.7rem",
            borderBottom: i < arr.length - 1 ? `1px solid ${border}` : "none",
            backgroundColor: highlight
              ? (isDark ? "rgba(29,158,117,0.05)" : "rgba(29,158,117,0.04)")
              : "transparent",
          }}>
            <Typography sx={{ fontSize: "12px", color: textSec, fontWeight: highlight ? 500 : 400 }}>
              {label}
            </Typography>
            <Typography sx={{ fontSize: highlight ? "17px" : "13px", fontWeight: highlight ? 600 : 500, color: highlight ? accent : textPri }}>
              {value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default InvoiceConfirmView;